/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import { jsx } from '@atlaskit/css';
import { Stack } from '@atlaskit/primitives/compiled';

import { ContributorIdOfNoPeopleGroup } from '../types';

const renameGroupMessage = defineMessage({
	id: 'capacity-planning.people-view.rename-group.non-final',
	defaultMessage: 'Rename',
	description:
		'Header label for the group of rename actions in the people view card that have no associated person.',
});

import { PersonGroup } from './PersonGroup';
import type { ContributorGroup, PeopleViewProps } from './types';

export function PeopleView({
	actionsByIndex: actionsIndex,
	hydratedUsers,
	workById,
	selectedKeys,
	onSelectionChange,
	onConfirm,
	onCancel,
	getStatus,
}: PeopleViewProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const contributorGroups = useMemo<ContributorGroup[]>(
		() =>
			Array.from(actionsIndex.actionsByContributorId.entries()).map(
				([contributorDataId, actions]) => ({
					contributorDataId,
					actions,
				}),
			),
		[actionsIndex.actionsByContributorId],
	);

	return (
		<Stack>
			{contributorGroups.map((group, index) => {
				const isNoPeopleGroup = group.contributorDataId === ContributorIdOfNoPeopleGroup;
				return (
					<PersonGroup
						key={group.contributorDataId}
						group={group}
						user={
							isNoPeopleGroup
								? { id: 'non-people', name: formatMessage(renameGroupMessage), picture: '' }
								: hydratedUsers.get(group.contributorDataId)!
						}
						isRenameGroup={isNoPeopleGroup}
						defaultExpanded={index === 0}
						selectedKeys={selectedKeys}
						onSelectionChange={onSelectionChange}
						workById={workById}
						onConfirm={onConfirm}
						onCancel={onCancel}
						getStatus={getStatus}
						hydratedUsers={hydratedUsers}
					/>
				);
			})}
		</Stack>
	);
}
